import React from 'react';
import { Card } from './Card';
import { Text } from '@/components/ui/text/Text';
import { IconScout, IconScoutName } from '@/components/ui/images/IconScout';
import { colors } from '@/assets/styles/colors';


export type IndicatorCardVariant = 'default' | 'primary' | 'important' | 'success' | 'error' | 'alert';

interface IndicatorCardProps {
  label: string;
  value: string | number;
  supportingText?: string;
  icon?: IconScoutName;
  variant?: IndicatorCardVariant;
  padValue?: boolean | number;
  className?: string;
  'data-testid'?: string;
}

const getVariantColor = (variant: IndicatorCardVariant): string => {
  switch (variant) {
    case 'primary':
      return colors.primary.dash;
    case 'important':
      return colors.important.main;
    case 'success':
      return colors.feedback.success300;
    case 'error':
      return colors.feedback.error300;
    case 'alert':
      return colors.feedback.alert300;
    case 'default':
    default:
      return colors.grays.neutral33;
  }
};

const formatValue = (
  value: string | number,
  padValue?: boolean | number,
): string => {
  if (padValue === false) {
    return String(value);
  }

  const stringValue = String(value);
  const isNumericString = /^\d+$/.test(stringValue);

  if (typeof value !== 'number' && !isNumericString) {
    return stringValue;
  }

  const minDigits = padValue === true || padValue === undefined ? 2 : padValue;
  return stringValue.padStart(minDigits, '0');
};

export const IndicatorCard: React.FC<IndicatorCardProps> = ({
  label,
  value,
  supportingText,
  icon,
  variant = 'default',
  padValue = true,
  className,
  'data-testid': dataTestId,
}) => {
  const variantColor = getVariantColor(variant);

  return (
    <Card
      elevation={1}
      className={className}
      data-testid={dataTestId}
      padding="16px"
      style={{ minWidth: '150px', maxHeight: '125px' }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {/* Label */}
        <Text
          variant="subheader-medium"
          color={colors.primary.dash}
          data-testid={`${dataTestId}-label`}
        >
          {label}
        </Text>

        {/* Indicator Value with optional Icon */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
          {icon && (
            <IconScout
              name={icon}
              size="lg"
              color={variantColor}
              data-testid={`${dataTestId}-icon`}
            />
          )}
          <Text
            variant="header-4"
            color={variantColor}
            data-testid={`${dataTestId}-value`}
          >
            {formatValue(value, padValue)}
          </Text>
        </div>

        {/* Supporting Text */}
        {supportingText && (
          <Text
            variant="small-regular"
            color={colors.grays.neutral66}
            style={{ fontSize: '10px' }}
            data-testid={`${dataTestId}-supporting`}
          >
            {supportingText}
          </Text>
        )}
      </div>
    </Card>
  );
};
